/**
 * Vector path boolean operations — Tier 2 Group A PR-C.
 *
 * Thin wrapper around paper.js's set operations
 * (unite / subtract / intersect / exclude). Operates on
 * `VectorPath` values and produces a `VectorPath` result so the
 * rest of the editor never sees a paper.js type. Same convention
 * as `lib/projective-transform.ts` / `lib/warp-mesh.ts`: pure
 * input → pure output, no DOM mutation.
 *
 * paper.js is stateful — it requires a project before any path
 * can be constructed. We lazy-initialize the global project the
 * first time a boolean op runs; subsequent calls reuse it. The
 * project is intentionally headless (no canvas element); set
 * operations work entirely off the path data.
 *
 * Limitations / trade-offs:
 *   - paper.js represents tangent handles relative to the
 *     segment point (same convention as `VectorAnchor`), so the
 *     paper ↔ VectorPath conversion is lossless on the round-
 *     trip. Curves stay curves.
 *   - Result paths can be **multi-sub-path** (when the union
 *     produces disconnected pieces or holes). Our `VectorPath`
 *     already models that.
 *   - paper.js's anchor type isn't preserved across set ops —
 *     it just produces points + handles. We mark every result
 *     anchor as `'smooth'` when it has handles, `'corner'`
 *     otherwise. Future enhancement: classify based on handle
 *     symmetry.
 */

// Use paper's headless `paper-core` entry point — same API
// surface but skips the canvas/rendering layer paper would
// otherwise initialize at module load. We never use paper to
// render; the boolean-op outputs flow back into our own
// VectorPath + Fabric rendering pipeline. Crucial for happy-dom
// tests where the default `paper` import fails on canvas init.
import paper from 'paper/dist/paper-core';
import type { VectorAnchor, VectorPath } from '../types';
import { pathToSvgData } from './vector-path';
import { BOOLEAN_OP_LABELS, type BooleanOp } from './path-ops.types';
import { ensurePaperInitialized } from './paper-bootstrap';

// Re-exported here so callers that already pull `pathBoolean`
// from this module don't need a second import for the type/labels.
export { BOOLEAN_OP_LABELS };
export type { BooleanOp };

/** Compute a boolean op on two vector paths and return the
 *  result as a fresh `VectorPath`. Inputs are not mutated.
 *  Returns an empty path (no sub-paths) when the operation
 *  produces no geometry (e.g., intersection of disjoint shapes).
 *
 *  Style fields (stroke / fill) are NOT propagated — the caller
 *  is expected to apply style from one of the source layers or
 *  from the active shape settings. paper.js operates on geometry
 *  only. */
export function pathBoolean(pathA: VectorPath, pathB: VectorPath, op: BooleanOp): VectorPath {
  ensurePaperInitialized();
  const dataA = pathToSvgData(pathA);
  const dataB = pathToSvgData(pathB);
  if (!dataA || !dataB) return { subPaths: [] };

  // CompoundPath accepts multi-`M` SVG data (multi-sub-path
  // inputs). For single-sub-path inputs it behaves like a
  // regular Path. paper.PathItem (CompoundPath's parent) exposes
  // every boolean op; the typing is loose so we cast.
  const paperA = new paper.CompoundPath({ pathData: dataA, insert: false });
  const paperB = new paper.CompoundPath({ pathData: dataB, insert: false });
  // Closed-fill semantics drive the boolean — open paths produce
  // unexpected results. Force closed on both inputs to mirror
  // SVG `fill-rule: nonzero` behavior.
  setAllClosed(paperA, true);
  setAllClosed(paperB, true);

  const opMap: Record<BooleanOp, keyof paper.PathItem> = {
    unite: 'unite',
    subtract: 'subtract',
    intersect: 'intersect',
    exclude: 'exclude',
  };
  // paper.PathItem boolean ops return a fresh PathItem (Path or
  // CompoundPath). Cast through unknown — paper's types decorate
  // each method with a specific return type union we don't need
  // here.
  const result = (paperA[opMap[op]] as (other: paper.PathItem) => paper.PathItem).call(
    paperA,
    paperB,
  );

  try {
    return paperPathItemToVectorPath(result);
  } finally {
    // Detach everything from paper's global project so successive
    // calls don't accumulate orphan PathItems.
    result.remove();
    paperA.remove();
    paperB.remove();
  }
}

/** Walk a paper.PathItem (Path | CompoundPath) and produce a
 *  fresh `VectorPath` with one sub-path per child. Each segment
 *  maps to a `VectorAnchor`; paper handles are already relative
 *  to the segment point so no offset math is needed. */
export function paperPathItemToVectorPath(item: paper.PathItem): VectorPath {
  const subPaths: VectorPath['subPaths'] = [];
  const visit = (path: paper.Path) => {
    if (!path.segments || path.segments.length === 0) return;
    const anchors: VectorAnchor[] = path.segments.map((seg) => {
      const hasIn = seg.handleIn && (seg.handleIn.x !== 0 || seg.handleIn.y !== 0);
      const hasOut = seg.handleOut && (seg.handleOut.x !== 0 || seg.handleOut.y !== 0);
      const anchor: VectorAnchor = {
        x: seg.point.x,
        y: seg.point.y,
        type: hasIn || hasOut ? 'smooth' : 'corner',
      };
      if (hasIn) {
        anchor.inX = seg.handleIn.x;
        anchor.inY = seg.handleIn.y;
      }
      if (hasOut) {
        anchor.outX = seg.handleOut.x;
        anchor.outY = seg.handleOut.y;
      }
      return anchor;
    });
    subPaths.push({ closed: path.closed === true, anchors });
  };

  if (isCompoundPath(item)) {
    for (const child of item.children) {
      if (isPath(child)) visit(child);
    }
  } else if (isPath(item)) {
    visit(item);
  }
  return { subPaths };
}

function setAllClosed(item: paper.PathItem, closed: boolean): void {
  if (isCompoundPath(item)) {
    for (const child of item.children) {
      if (isPath(child)) child.closed = closed;
    }
  } else if (isPath(item)) {
    item.closed = closed;
  }
}

function isCompoundPath(item: paper.Item): item is paper.CompoundPath {
  return item instanceof paper.CompoundPath;
}

function isPath(item: paper.Item): item is paper.Path {
  return item instanceof paper.Path;
}
